/**
 * Stage Studios — premium homepage.
 *
 * Editorial luxury aesthetic: ivory ground, near-black ink, restrained warm-
 * gold accent, 24px rounded surfaces, soft layered shadows, generous white
 * space. Built for a billion-dollar proptech launch — the references are
 * Apple / Linear / Stripe / Raycast / Arc / Notion / Vercel / Porsche, NOT
 * other AI-staging companies.
 *
 * 13 sections, each animates into view with subtle motion. The signature
 * piece is `CompareExperience` (4-mode picker + draggable vertical slider).
 *
 * All luxury tokens are scoped under `.lux` so the rest of the app
 * (studio/billing/admin) is not affected.
 */
import { lazy, Suspense, useEffect, useRef, useState, ReactNode, MouseEvent as ReactMouseEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Sofa,
  Eraser,
  Wand2,
  Upload,
  Settings2,
  Download,
  ShieldCheck,
  Zap,
  Image as ImageIcon,
  Layers,
  Globe2,
  Lock,
  Star,
  Plus,
  Minus,
  Smartphone,
  Camera,
  X,
  RefreshCw,
  Plug,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import BrandLogo from "@/components/BrandLogo";
import api, { formatApiError } from "@/lib/api";
import type { Pack } from "@/types";
import "@/components/landing/landingStyles.css";

const CompareExperience = lazy(() => import("@/components/landing/CompareExperience"));

/* ============================================================
 * CMS-style content. Swap any image URL here — components auto-update.
 * ============================================================ */
const HERO_IMG =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=85";

const COMPARISONS = [
  {
    mode: "enhance",
    label: "Enhance",
    shortDescription: "Refine light, colour & clarity.",
    title: "The photograph you almost captured.",
    description:
      "Refine lighting, colour, composition and clarity while preserving every architectural detail. Sky balance, exposure correction, lens warmth — all without changing what's actually in the room.",
    beforeUrl: "https://i.ibb.co/HDvNcwqN/enhance-before.png",
    afterUrl: "https://i.ibb.co/1JPfHvST/enhance-after.jpg",
  },
  {
    mode: "furnish",
    label: "Furnish",
    shortDescription: "Empty rooms, beautifully styled.",
    title: "From bare walls to liveable luxury.",
    description:
      "Transform empty properties into beautifully furnished luxury homes. Sofas, art, lighting, plants — selected to suit the room's geometry and the property's price tier.",
    beforeUrl: "https://i.ibb.co/VYFHcbLw/furnish-before.png",
    afterUrl: "https://i.ibb.co/d0VHf9qK/furnish-after.jpg",
  },
  {
    mode: "prepare",
    label: "Prepare",
    shortDescription: "Strip clutter. Sharpen the canvas.",
    title: "A clean canvas for the next chapter.",
    description:
      "Remove clutter, personal items and visual noise while preparing every image for premium staging downstream. Floors, walls and ceilings stay exactly where they were.",
    beforeUrl: "https://i.ibb.co/NdNbf9Jp/prepare-before.png",
    afterUrl: "https://i.ibb.co/5hsQHTXb/prepare-after.jpg",
  },
  {
    mode: "stage",
    label: "Stage",
    shortDescription: "Editorial-grade redesigns.",
    title: "Editorial-quality interiors. On demand.",
    description:
      "Completely redesign interiors into editorial-quality luxury spaces. Architectural geometry, windows and ceilings remain locked — only the furnishings, finishes and atmosphere change.",
    beforeUrl: "https://i.ibb.co/Tq2cRhYw/stage-before.png",
    afterUrl: "https://i.ibb.co/zVGbctcB/stage-after.jpg",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "We doubled our listing engagement in the first month. The Stage mode produces images we'd previously have paid a stylist five thousand dirhams for.",
    name: "Layla Al-Mansoori",
    role: "Sales Director, Mirage Realty",
    avatar:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80",
    rating: 5,
  },
  {
    quote:
      "The detail retention is what won me over. Walls, ceilings and window mullions stay locked — only the styling changes. It's the only AI tool I now trust for client work.",
    name: "Marcus Ferreira",
    role: "Architectural Photographer, Lisbon",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=300&q=80",
    rating: 5,
  },
  {
    quote:
      "I ship a hundred photos a week through Stage Studios. The bulk pipeline is faster than my old retoucher and the brand stays consistent across every listing.",
    name: "Priya Raghavan",
    role: "Marketing Lead, Aurelia Developments",
    avatar:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80",
    rating: 5,
  },
];

const FAQS = [
  {
    q: "How fast do I get my photos back?",
    a: "About 30 to 60 seconds per photo. A 10-photo batch typically completes in 5 to 10 minutes. You see live progress per image and download as each one finishes — no need to wait for the entire batch.",
  },
  {
    q: "What counts as one credit?",
    a: "One credit edits one photo. If a particular edit fails on our end, that credit is refunded automatically — you only pay for successful results.",
  },
  {
    q: "Do you stage empty rooms or just enhance furnished ones?",
    a: "Both — and two more. Choose Furnish for empty rooms, Stage for full editorial redesigns, Enhance for clean-up only, and Prepare to strip clutter before downstream staging.",
  },
  {
    q: "Will my room geometry, windows, and ceiling stay the same?",
    a: "Yes. The model locks the camera angle, walls, windows, doors, floor plan, and ceiling height. Only removable items (furniture, decor, lighting) change.",
  },
  {
    q: "Can I cancel a subscription anytime?",
    a: "Yes. Subscriptions cancel at the end of the current period — no early-termination fees and no contracts. Credits already deposited are yours to keep.",
  },
  {
    q: "Do I need a PayPal account to buy credits?",
    a: "No. We accept any major credit or debit card through PayPal's guest checkout — no PayPal account required. You can also use PayPal, Pay Later, or Venmo if you prefer.",
  },
  {
    q: "How long do my credits last?",
    a: "Free trial credits last 30 days. Paid credits last 6 months from purchase — and each new top-up resets that 6-month timer on your whole balance, so regular users keep their credits indefinitely.",
  },
];

/* ============================================================
 * Shared motion variants
 * ============================================================ */
const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};
const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.9, ease: "easeOut" },
};

/* ============================================================
 * Page
 * ============================================================ */
export default function Landing() {
  const { user } = useAuth();
  const [crmRequestOpen, setCrmRequestOpen] = useState(false);
  if (user) return <Navigate to={user.is_admin ? "/admin" : "/studio"} replace />;

  return (
    <div className="lux min-h-screen overflow-x-hidden">
      {/* Skip to content (a11y) */}
      <a
        href="#main"
        tabIndex={1}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-[var(--lux-ink)] focus:text-white focus:text-sm focus:rounded-full"
        data-testid="skip-to-content"
      >
        Skip to main content
      </a>

      <LuxuryNav />
      <Hero />
      <TrustLine />
      <Suspense fallback={<div style={{ minHeight: 600 }} />}>
        <CompareSection />
      </Suspense>
      <ModesSection />
      <WorkflowSection />
      <NoPhotographerSection />
      <WhySection />
      <CrmSyncSection onRequestClick={() => setCrmRequestOpen(true)} />
      <FeatureGrid />
      <TestimonialsSection />
      <PricingPreview />
      <LandingRoadmapSection />
      <FaqSection />
      <FinalCTA />
      <LuxuryFooter />
      <CrmRequestModal open={crmRequestOpen} onClose={() => setCrmRequestOpen(false)} />
    </div>
  );
}

/* ============================================================
 * 1 · Luxury navigation
 * ============================================================ */
function LuxuryNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      className={`lux-nav-glass sticky top-0 z-40 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_4px_24px_-12px_rgba(0,0,0,0.08)]" : ""
      }`}
      data-testid="luxury-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <BrandLogo variant="wide" className="h-9 w-auto" />
        </Link>
        <div className="hidden md:flex items-center gap-8 text-[14px]">
          <a href="#modes" className="lux-underline-gold text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors" data-testid="nav-modes-link">Modes</a>
          <a href="#workflow" className="lux-underline-gold text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors" data-testid="nav-workflow-link">Workflow</a>
          <a href="#pricing" className="lux-underline-gold text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors" data-testid="nav-pricing-link">Pricing</a>
          <Link to="/roadmap" className="lux-underline-gold text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors" data-testid="nav-roadmap-link">Roadmap</Link>
          <a href="#faq" className="lux-underline-gold text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors" data-testid="nav-faq-link">FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          <CurrencySwitcher variant="ghost" testid="landing-currency-switcher" />
          <Link to="/login" className="inline-flex text-[13px] sm:text-[14px] font-medium text-[var(--lux-ink-soft)] hover:text-[var(--lux-ink)] transition-colors px-2 sm:px-3 py-2" data-testid="nav-login-link">
            Sign in
          </Link>
          <Link to="/register" className="lux-btn-primary text-[13px] py-2.5 px-5" data-testid="nav-register-btn">
            Stage Free <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* ============================================================
 * 2 · Hero
 * ============================================================ */

/* iter49 P3-J: shared hook for the lowest per-credit price across the
 * landing page. Replaces two near-identical fetch blocks (HeroFromPrice +
 * NoPhotographerFromPrice) so the page only ever issues ONE /api/credits/packs
 * request, and the two consumers stay in sync forever. */
function useLowestCreditPrice() {
  const [lowest, setLowest] = useState<number | null>(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/credits/packs`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const packs: Pack[] = Array.isArray(d?.packs) ? d.packs : [];
        if (packs.length === 0) return;
        const min = packs.reduce((acc: number, p: Pack) => (p.per_credit_usd < acc ? p.per_credit_usd : acc), Infinity);
        setLowest(Number.isFinite(min) ? min : null);
      })
      .catch(() => { /* keep fallback */ });
  }, []);
  return lowest;
}

/* Live "From X / photo" caption — pulls the lowest per_credit_usd from
 * /api/credits/packs and formats it in the user's chosen display currency.
 *
 * iter49 P3-B: shows a skeleton placeholder while loading instead of the
 * old static "$0.33" fallback that flashed and was replaced. A discerning
 * luxury-brand visitor sees a stable layout — no text-jank flicker. */
function HeroFromPrice() {
  const { formatMoney } = useCurrency();
  const lowest = useLowestCreditPrice();
  if (lowest === null) {
    return (
      <span
        className="inline-block w-28 h-5 bg-[var(--lux-line)]/40 animate-pulse rounded align-middle"
        aria-busy="true"
        aria-label="Loading price"
        data-testid="hero-from-price-skeleton"
      />
    );
  }
  return (
    <span data-testid="hero-from-price">From <strong className="text-[var(--lux-ink)] font-semibold">{formatMoney(lowest)} / photo</strong></span>
  );
}

function Hero() {
  // Subtle parallax on the hero image as the user scrolls.
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} id="main" tabIndex={-1} className="relative pt-16 md:pt-24 pb-24 md:pb-36 focus:outline-none" data-testid="hero">
      {/* Ambient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="lux-orb lux-orb-gold lux-orb-drift-1" style={{ width: 520, height: 520, top: -120, left: -120 }} />
        <div className="lux-orb lux-orb-ivory lux-orb-drift-2" style={{ width: 480, height: 480, bottom: -160, right: -80 }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              className="lux-eyebrow mb-6"
            >
              Luxury Virtual Staging · Built for Premium Real Estate
            </motion.div>

            <h1 className="lux-display text-[2.75rem] sm:text-6xl lg:text-[5.4rem] leading-[0.98] tracking-[-0.02em] mb-8">
              <HeadlineWord delay={0.05}>The photograph</HeadlineWord>{" "}
              <HeadlineWord delay={0.15}>that</HeadlineWord>
              <br />
              <HeadlineWord delay={0.25} italic>sells the home.</HeadlineWord>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
              className="text-[16px] md:text-[18px] leading-[1.65] max-w-xl mb-10"
              style={{ color: "var(--lux-ink-soft)" }}
            >
              No expensive photographer. No retoucher. A broker, a leasing agent, or even the tenant can shoot a single phone photo — Stage Studios turns it into editorial-grade marketing imagery in minutes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
              className="flex flex-wrap items-center gap-3"
            >
              <MagneticButton to="/register" testid="hero-cta-btn" primary>
                Stage Free <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </MagneticButton>
              <MagneticButton href="#compare" testid="hero-secondary-btn">
                Watch demo
              </MagneticButton>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="flex items-center gap-6 mt-10 text-[12px] tracking-wide"
              style={{ color: "var(--lux-muted)" }}
            >
              <span><strong className="text-[var(--lux-ink)] font-semibold">5 photos free</strong> · no card required</span>
              <span className="hidden sm:inline" aria-hidden>·</span>
              <span className="hidden sm:inline"><HeroFromPrice /></span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] shadow-[var(--lux-shadow-deep)]">
              <motion.img
                src={HERO_IMG}
                alt="An editorial interior captured by Stage Studios"
                className="w-full h-full object-cover lux-breathe"
                style={{ y: imgY }}
                loading="eager"
              />
              {/* Floating credential card — live data from /api/public/recent-generation */}
              <NowGeneratingCard />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * Live "Now generating" ticker. Polls `/api/public/recent-generation` every
 * 60 seconds, formats the seconds-ago into human-readable text, and shows a
 * pulsing gold dot when `is_live=true` (last activity within 30min).
 *
 * Fully anonymized — the endpoint never returns user/email/job/photo ids.
 */
function NowGeneratingCard() {
  const [data, setData] = useState({
    mode_label: "Stage", room_label: "Living room", style_label: "Editorial",
    seconds_ago: null, is_live: false,
  });
  const [stats, setStats] = useState<{ today: number | null; all_time: number | null }>({ today: null, all_time: null });
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/api/public/recent-generation`),
          fetch(`${API}/api/public/stats/today`),
        ]);
        if (r1.ok) {
          const d = await r1.json();
          if (!cancelled) setData(d);
        }
        if (r2.ok) {
          const s = await r2.json();
          if (!cancelled) setStats(s);
        }
      } catch {
        // silent
      }
    };
    fetchAll();
    const t = setInterval(fetchAll, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const recency = (() => {
    const s = data?.seconds_ago;
    if (s == null) return "just moments ago";
    if (s < 30) return "just now";
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return "today";
  })();
  const todayCount = typeof stats.today === "number" ? stats.today.toLocaleString() : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1, duration: 0.7 }}
      className="absolute bottom-4 left-4 right-4 lux-card px-4 py-3"
      data-testid="now-generating-card"
    >
      {/* Top row: today's count (social proof) */}
      {todayCount !== null && (
        <div
          className="flex items-baseline gap-2 pb-2.5 mb-2.5 border-b border-[var(--lux-line)]"
          data-testid="stats-today-row"
        >
          <span className="lux-display text-[1.3rem] leading-none tracking-tight" data-testid="stats-today-count">
            {todayCount}
          </span>
          <span className="text-[12px]" style={{ color: "var(--lux-muted)" }}>
            photographs staged {data.seconds_ago != null && data.seconds_ago < 86400 ? "today" : "this week"}
          </span>
        </div>
      )}
      {/* Bottom row: latest result */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "var(--lux-gold-deep)" }}>Latest result</div>
          <div className="text-[14px] font-medium mt-0.5 truncate" data-testid="now-generating-text">
            {data.mode_label} · {data.room_label} · {data.style_label}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div
            className={`w-2 h-2 rounded-full ${data.is_live ? "bg-[var(--lux-gold)] animate-pulse" : "bg-[var(--lux-muted)]"}`}
            aria-hidden
          />
          <span className="text-[12px] text-[var(--lux-muted)]" data-testid="now-generating-recency">{recency}</span>
        </div>
      </div>
    </motion.div>
  );
}

interface HeadlineWordProps {
  children: ReactNode;
  delay: number;
  italic?: boolean;
}

function HeadlineWord({ children, delay, italic }: HeadlineWordProps) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.85, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className={`inline-block ${italic ? "italic font-light" : ""}`}
    >
      {children}
    </motion.span>
  );
}

interface MagneticButtonProps {
  to?: string;
  href?: string;
  children: ReactNode;
  testid?: string;
  primary?: boolean;
}

function MagneticButton({ to, href, children, testid, primary }: MagneticButtonProps) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const [m, setM] = useState({ x: 0, y: 0 });
  function onMove(e: ReactMouseEvent) {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setM({ x: (e.clientX - r.left - r.width / 2) * 0.18, y: (e.clientY - r.top - r.height / 2) * 0.18 });
  }
  const className = primary ? "lux-btn-primary lux-mag" : "lux-btn-ghost lux-mag";
  const inner = (
    <span style={{ transform: `translate(${m.x}px, ${m.y}px)` }} className="inline-flex items-center gap-2">
      {children}
    </span>
  );
  const props = {
    ref,
    className,
    onMouseMove: onMove,
    onMouseLeave: () => setM({ x: 0, y: 0 }),
    "data-testid": testid,
  };
  return to ? <Link to={to} {...props}>{inner}</Link> : <a href={href} {...props}>{inner}</a>;
}

/* ============================================================
 * 3 · Trust line (single credentials sentence per user choice)
 * ============================================================ */
function TrustLine() {
  return (
    <motion.section {...fadeIn} className="border-y border-[var(--lux-line)] bg-[var(--lux-bg)]/60" data-testid="trust-line">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-center gap-4 text-[12px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--lux-muted)" }}>
        <span className="hidden sm:inline-block w-12 h-px bg-[var(--lux-line-strong)]" aria-hidden />
        <span>Trusted by listing teams across UAE, KSA &amp; London</span>
        <span className="hidden sm:inline-block w-12 h-px bg-[var(--lux-line-strong)]" aria-hidden />
      </div>
    </motion.section>
  );
}

/* ============================================================
 * 4 · Signature compare experience
 * ============================================================ */
function CompareSection() {
  return (
    <section id="compare" className="relative py-24 md:py-36" data-testid="compare-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 lux-orb lux-orb-gold" style={{ width: 700, height: 700, opacity: 0.32 }} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <motion.div {...fadeUp} className="max-w-3xl mb-14 md:mb-20">
          <div className="lux-eyebrow mb-4">The Stage Studios experience</div>
          <h2 className="lux-display text-4xl md:text-[3.5rem] leading-[1.04] tracking-tight">
            Four modes.<br />
            <span className="italic font-light">One studio.</span>
          </h2>
          <p className="text-[15px] md:text-[17px] leading-[1.7] mt-6 max-w-2xl" style={{ color: "var(--lux-ink-soft)" }}>
            Pick a mode to redirect the studio. Drag the slider to compare what we changed — and, critically, everything we didn&apos;t.
          </p>
        </motion.div>

        <Suspense fallback={<div className="aspect-[16/9] rounded-[24px] bg-[var(--lux-line)]/40 animate-pulse" />}>
          <CompareExperience comparisons={COMPARISONS} defaultMode="stage" />
        </Suspense>
      </div>
    </section>
  );
}

/* ============================================================
 * 5 · Four editing modes — explanatory grid
 * ============================================================ */
function ModesSection() {
  const modes = [
    {
      icon: Sparkles,
      name: "Enhance",
      copy: "Refine lighting, colour, composition and clarity while preserving every architectural detail.",
      uses: ["Daylight balance", "Lens warmth", "Shadow recovery"],
    },
    {
      icon: Sofa,
      name: "Furnish",
      copy: "Transform empty properties into beautifully furnished luxury homes — selected to suit the room's geometry.",
      uses: ["Empty rooms", "Off-plan listings", "Show-home dressing"],
    },
    {
      icon: Eraser,
      name: "Prepare",
      copy: "Remove clutter, personal items and visual noise. A clean canvas, ready for downstream staging.",
      uses: ["Tenant-occupied homes", "Estate sales", "Pre-stage prep"],
    },
    {
      icon: Wand2,
      name: "Stage",
      copy: "Completely redesign interiors into editorial-quality luxury spaces — architecture locked, atmosphere transformed.",
      uses: ["Magazine listings", "High-end developers", "Portfolio rebrands"],
    },
  ];
  return (
    <section id="modes" className="py-24 md:py-32" data-testid="modes-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <motion.div {...fadeUp} className="md:col-span-5">
            <div className="lux-eyebrow mb-4">Choose your direction</div>
            <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
              Every listing has<br />a different brief.
            </h2>
          </motion.div>
          <motion.p {...fadeUp} className="md:col-span-6 md:col-start-7 self-end text-[15px] leading-[1.7]" style={{ color: "var(--lux-ink-soft)" }}>
            The same studio, four lenses. Use Stage for editorial redesigns, Furnish for empty rooms, Prepare to strip clutter, or Enhance to clean what&apos;s already great.
          </motion.p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {modes.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
              whileHover={{ y: -4 }}
              className="lux-card p-7 group"
            >
              <div className="w-11 h-11 rounded-full grid place-items-center mb-7" style={{ background: "rgba(181,138,69,0.10)", color: "var(--lux-gold-deep)" }}>
                <m.icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <div className="lux-display text-[1.7rem] mb-3">{m.name}</div>
              <p className="text-[14.5px] leading-[1.65] mb-6" style={{ color: "var(--lux-ink-soft)" }}>{m.copy}</p>
              <div className="lux-hr-gold mb-4" />
              <ul className="space-y-1.5">
                {m.uses.map((u) => (
                  <li key={u} className="text-[12.5px] flex items-center gap-2" style={{ color: "var(--lux-muted)" }}>
                    <span className="w-1 h-1 rounded-full bg-[var(--lux-gold)]" />
                    {u}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 6 · Workflow
 * ============================================================ */
function WorkflowSection() {
  const steps = [
    { icon: Upload, num: "01", title: "Upload your photographs", body: "Drag and drop in bulk, paste image URLs, or import directly from your listing CRM. Originals stay intact." },
    { icon: Settings2, num: "02", title: "Choose a mode and direction", body: "Enhance, Furnish, Prepare or Stage — with per-image room types and styles if you need granular control." },
    { icon: Download, num: "03", title: "Download a magazine-ready set", body: "Pull individual photos or the whole batch as a zip. Failures auto-refund. Credits never expire on active accounts." },
  ];
  return (
    <section id="workflow" className="border-t border-[var(--lux-line)] py-24 md:py-32 bg-white/40" data-testid="workflow-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-3xl mb-16 md:mb-20">
          <div className="lux-eyebrow mb-4">The workflow</div>
          <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
            Three steps.<br /><span className="italic font-light">Five minutes.</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-8 relative">
          {/* Connecting gold line */}
          <div className="hidden md:block absolute left-[12%] right-[12%] top-[42px] h-px" style={{ background: "linear-gradient(90deg, transparent, var(--lux-gold-glow), transparent)" }} aria-hidden />
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative"
            >
              <div className="w-[84px] h-[84px] rounded-full grid place-items-center mb-7 lux-card" style={{ background: "var(--lux-bg)" }}>
                <s.icon className="w-7 h-7" strokeWidth={1.4} style={{ color: "var(--lux-ink)" }} />
              </div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-2" style={{ color: "var(--lux-gold-deep)" }}>Step {s.num}</div>
              <h3 className="lux-display text-[1.75rem] leading-[1.15] mb-3">{s.title}</h3>
              <p className="text-[14.5px] leading-[1.7]" style={{ color: "var(--lux-ink-soft)" }}>{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 6b · No-photographer-required (disruption beat)
 * ============================================================ */
function NoneNeededPill({ label }: { label: string }) {
  // Crossed-out role pill used in the NoPhotographerSection. Pulled out of the
  // parent body so React doesn't recreate the component on every render
  // (eslint react/no-unstable-nested-components).
  return (
    <span
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--lux-line-strong)] text-[13px] tracking-wide"
      style={{ color: "var(--lux-muted)" }}
    >
      <X className="w-3.5 h-3.5" strokeWidth={2} />
      <span className="line-through decoration-1">{label}</span>
    </span>
  );
}

/* Currency-aware "typical cost" range. AED 1,800–6,000 / listing is the
 * editorial reference point — convert each bound from AED into the active
 * currency using the live FX rate. */
function NoPhotographerTypicalCost() {
  const { formatMoney, rates } = useCurrency();
  const aedRate = rates?.AED || 3.6725;
  // The reference figures are in AED → reverse-convert to USD then re-format.
  const lowUsd  = 1800 / aedRate;
  const highUsd = 6000 / aedRate;
  return (
    <span data-testid="no-photographer-typical-cost">{formatMoney(lowUsd, { decimals: 0 })} – {formatMoney(highUsd, { decimals: 0 })}</span>
  );
}

/* Currency-aware "from X per photo" headline. AED 0.74 ≈ USD 0.20.
 * We use the live lowest per-credit price so this stays in sync with packs.
 *
 * iter49 P3-B/J: uses the shared useLowestCreditPrice hook + skeleton
 * placeholder so the layout doesn't jank when packs resolve. */
function NoPhotographerFromPrice() {
  const { formatMoney } = useCurrency();
  const lowest = useLowestCreditPrice();
  if (lowest === null) {
    return (
      <span
        className="inline-block w-20 h-4 bg-[var(--lux-line)]/40 animate-pulse rounded align-middle"
        aria-busy="true"
        aria-label="Loading price"
        data-testid="no-photographer-from-price-skeleton"
      />
    );
  }
  return <span data-testid="no-photographer-from-price">From {formatMoney(lowest)}</span>;
}

function NoPhotographerSection() {
  // Three crossed-out role pills + arrow + a phone-camera node + arrow + the
  // result. Pure typographic poster — no images, fully responsive, every
  // letter sized for editorial impact.
  return (
    <section className="py-24 md:py-36 relative overflow-hidden" data-testid="no-photographer-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="lux-orb lux-orb-gold lux-orb-drift-2" style={{ width: 540, height: 540, top: -100, right: -120, opacity: 0.45 }} aria-hidden />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <motion.div {...fadeUp} className="max-w-4xl">
          <div className="lux-eyebrow mb-5">A new economic possibility</div>
          <h2 className="lux-display text-4xl md:text-[3.6rem] leading-[1.05] tracking-tight mb-8">
            No photographer.<br/>
            No retoucher.<br/>
            <span className="italic font-light">No problem.</span>
          </h2>
          <p className="text-[16px] md:text-[17px] leading-[1.7] max-w-2xl" style={{ color: "var(--lux-ink-soft)" }}>
            A broker, a leasing agent, even the current tenant can shoot a single phone photo of an occupied unit — laundry on the sofa, dishes in the sink, the lot. Stage Studios handles the rest: declutter, restyle, relight, and deliver editorial-grade marketing imagery in minutes.
          </p>
        </motion.div>

        {/* Visual sequence: crossed-out roles → phone → result */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-14 lux-card p-8 md:p-12 grid md:grid-cols-12 gap-10 items-start"
        >
          {/* Before/never-needed roles */}
          <div className="md:col-span-5 space-y-3 md:pt-4">
            <div className="lux-eyebrow mb-4" style={{ color: "var(--lux-muted)" }}>You used to need</div>
            <div className="flex flex-wrap gap-2.5">
              <NoneNeededPill label="A property photographer" />
              <NoneNeededPill label="A photo retoucher" />
              <NoneNeededPill label="A staging crew" />
              <NoneNeededPill label="A second site visit" />
            </div>
            <div className="mt-6 text-[13px] tracking-wide" style={{ color: "var(--lux-muted)" }}>
              Typical cost · <strong className="text-[var(--lux-ink)]"><NoPhotographerTypicalCost /></strong> per listing
            </div>
            <div className="mt-8 pt-6 border-t border-[var(--lux-line)]">
              <div className="lux-eyebrow mb-3" style={{ color: "var(--lux-gold-deep)" }}>What it costs now</div>
              <div className="lux-display text-[2rem] leading-none mb-1"><NoPhotographerFromPrice /></div>
              <div className="text-[12.5px]" style={{ color: "var(--lux-muted)" }}>per photo · failed edits never charged</div>
            </div>
          </div>

          {/* Phone-to-result transformation — 6-panel workflow collage.
              iter49 P3-I: hidden on mobile because mobile users already
              face a long scroll on this page; the image is visually dense
              and adds significant vertical real-estate. Desktop keeps the
              full editorial impact. */}
          <div className="md:col-span-7 hidden lg:block">
            {/* iter49 — 6-panel workflow grid. Replaces the previous single
                composite image with 6 individual lazy-loaded panels. Sharper
                detail (each panel is its own asset, no JPEG-of-JPEG quality
                loss), independent loading priority (the first 2 panels paint
                immediately, the rest stream in as the user scrolls), and
                lets the operator swap individual stages without re-rendering
                the whole composite. */}
            <div className="relative grid grid-cols-3 grid-rows-2 gap-2.5">
              {[
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/ekrou7h3_Screenshot%202026-06-28%20at%2011.32.20%E2%80%AFpm.png", alt: "1 of 6 — Cluttered home interior before staging", priority: "high" },
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/d38jw3oe_Screenshot%202026-06-28%20at%2011.32.24%E2%80%AFpm.png", alt: "2 of 6 — A phone framing the room for capture", priority: "high" },
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/i9k10z6x_Screenshot%202026-06-28%20at%2011.32.29%E2%80%AFpm.png", alt: "3 of 6 — Uploading the photo to Stage Studios", priority: "low" },
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/66ktmco8_Screenshot%202026-06-28%20at%2011.32.34%E2%80%AFpm.png", alt: "4 of 6 — AI enhancing in progress (20-45 seconds)", priority: "low" },
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/17l5zjyv_Screenshot%202026-06-28%20at%2011.32.39%E2%80%AFpm.png", alt: "5 of 6 — Reviewing the staged result in preview", priority: "low" },
                { src: "https://customer-assets.emergentagent.com/job_phase2-orchestration/artifacts/hzxqxcg9_Screenshot%202026-06-28%20at%2011.32.52%E2%80%AFpm.png", alt: "6 of 6 — Final editorial-grade marketing image", priority: "low" },
              ].map((panel, idx) => (
                <div
                  key={idx}
                  className="relative rounded-[12px] overflow-hidden bg-[var(--lux-cream)]"
                  style={{ boxShadow: idx === 5 ? "var(--lux-shadow-lifted)" : "0 4px 12px -6px rgba(10,10,9,0.12)" }}
                >
                  <img
                    src={panel.src}
                    alt={panel.alt}
                    className="w-full h-auto object-cover aspect-square"
                    loading={panel.priority === "high" ? "eager" : "lazy"}
                    fetchPriority={panel.priority as "auto" | "high" | "low"}
                  />
                  {/* Subtle step number — luxury editorial touch */}
                  <div
                    aria-hidden
                    className="absolute top-2 left-2 text-[10px] font-semibold tracking-[0.2em] px-1.5 py-0.5 rounded-sm"
                    style={{ background: "rgba(255,255,255,0.92)", color: "var(--lux-ink)" }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center text-[11px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--lux-muted)" }}>
              Phone photo · Upload · AI processing · Edit-ready · Done
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================================
 * 7 · Why Stage Studios
 * ============================================================ */
function WhySection() {
  return (
    <section className="py-24 md:py-32" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <div className="lux-eyebrow mb-4">Why Stage Studios</div>
            <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight mb-6">
              Built for the listings<br />
              <span className="italic font-light">that matter.</span>
            </h2>
            <p className="text-[15px] md:text-[16px] leading-[1.7]" style={{ color: "var(--lux-ink-soft)" }}>
              Generic AI tools were trained on consumer photos. We trained ours on a decade of luxury listing photography — and we lock the architecture so the home you photographed is still the home you sell.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 lux-card-dark p-10 md:p-12">
            <div className="lux-eyebrow mb-6" style={{ color: "var(--lux-gold)" }}>The Stage Studios standard</div>
            <ul className="space-y-6">
              {[
                "Architecture-locked generations — windows, walls, ceilings, doorways never move.",
                "Editorial styling library matched to the property's price tier and region.",
                "Bulk pipeline with per-image room overrides — no chat, no per-photo babysitting.",
                "Failed credits auto-refund. You only pay for results you can actually publish.",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-4 text-[15px] text-white/85 leading-[1.6]">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[var(--lux-gold)] shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================================
 * 7b · CRM sync — Kendal.io live + more on request
 * ============================================================ */
const CRM_PARTNERS = [
  {
    name: "Kendal.io",
    logo: "/crm-logos/kendal.png",
    live: true,
    region: "Global / UAE",
    note: "Live integration",
    capability: "Pull listings & photos",
  },
  {
    name: "Bitrix24",
    logo: "/crm-logos/bitrix24.png",
    live: true,
    region: "Global / UAE",
    note: "Live integration",
    capability: "Pull listings & photos via XML feed",
  },
  {
    name: "Goyzer",
    logo: "/crm-logos/goyzer.png",
    live: false,
    region: "UAE / MENA",
    note: "Coming soon · on request",
    capability: null,
  },
  {
    name: "Salesforce",
    logo: "/crm-logos/salesforce.png",
    live: false,
    region: "Global",
    note: "Coming soon · on request",
    capability: null,
  },
  {
    name: "Propspace",
    logo: "/crm-logos/propspace.png",
    live: false,
    region: "UAE",
    note: "Coming soon · on request",
    capability: null,
  },
  {
    name: "PropHero",
    logo: "/crm-logos/prophero.png",
    live: false,
    region: "AU / EU",
    note: "Coming soon · on request",
    capability: null,
  },
];

function CrmSyncSection({ onRequestClick }: { onRequestClick: () => void }) {
  return (
    <section id="crm-sync" className="py-24 md:py-32 border-t border-[var(--lux-line)] bg-white/40" data-testid="crm-sync-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-16">
          <div className="md:col-span-6">
            <div className="lux-eyebrow mb-4">CRM Sync</div>
            <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
              Sync your listings.<br/>
              <span className="italic font-light">Skip the upload.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-[15px] md:text-[16px] leading-[1.7]" style={{ color: "var(--lux-ink-soft)" }}>
              Stage Studios connects to your CRM and pulls every new listing&apos;s photographs automatically — from Kendal.io or a Bitrix24 XML feed.
            </p>
            <div className="mt-5 flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--lux-gold-deep)" }}>
              <Plug className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span>Live today · Kendal.io &amp; Bitrix24</span>
            </div>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {CRM_PARTNERS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
              className="lux-card p-6 flex flex-col gap-5"
              data-testid={`crm-card-${p.name.toLowerCase().replace(/\W+/g, "-")}`}
            >
              {/* Logo plate — fixed-height transparent box so every card aligns,
                  while the brand-mark itself (with its own transparent
                  background) sits cleanly on the card surface. */}
              <div className="w-full h-20 rounded-xl flex items-center justify-center overflow-hidden border border-[var(--lux-line)] bg-transparent">
                <img
                  src={p.logo}
                  alt={`${p.name} logo`}
                  className="max-h-[52px] max-w-[78%] w-auto h-auto object-contain"
                  loading="lazy"
                />
              </div>

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="lux-display text-[1.15rem] tracking-tight truncate">{p.name}</div>
                  <div className="text-[12px] mt-1" style={{ color: "var(--lux-muted)" }}>{p.region}</div>
                  <div className="text-[12.5px] mt-2" style={{ color: p.live ? "var(--lux-gold-deep)" : "var(--lux-ink-soft)" }}>
                    {p.note}
                  </div>
                  {p.capability && (
                    <div className="text-[11.5px] mt-1.5 leading-snug" style={{ color: "var(--lux-muted)" }}>
                      {p.capability}
                    </div>
                  )}
                </div>
                {p.live ? (
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: "var(--lux-ink)", color: "#fff" }}>Live</span>
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 shrink-0 mt-1" strokeWidth={1.6} style={{ color: "var(--lux-muted)" }} aria-label="Coming soon" />
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-12 flex flex-wrap items-center justify-between gap-4"
        >
          <p className="text-[14px]" style={{ color: "var(--lux-ink-soft)" }}>
            Don&apos;t see your CRM? We integrate per request — usually within a fortnight.
          </p>
          <button
            type="button"
            onClick={onRequestClick}
            className="lux-btn-ghost"
            data-testid="crm-request-open"
          >
            Request your CRM <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================================
 * 7c · CRM request modal — posts to /api/public/crm-request
 * (lands in the same admin feedback inbox under category=integration)
 * ============================================================ */
function CrmRequestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [crmName, setCrmName] = useState("");
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCrmName(""); setCompany(""); setContactName(""); setContactEmail(""); setDetails("");
  };

  // Close on Escape — keeps modal feeling native to the rest of the luxury surface.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const valid =
    crmName.trim().length >= 1 &&
    contactName.trim().length >= 1 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail.trim());

  const submit = async () => {
    if (!valid) return toast.error("Please complete CRM name, your name, and a valid email.");
    setSubmitting(true);
    try {
      await api.post("/public/crm-request", {
        crm_name: crmName.trim(),
        company: company.trim() || undefined,
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        details: details.trim() || undefined,
      });
      toast.success("Thanks — we'll be in touch about your CRM integration.");
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4" data-testid="crm-request-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
        className="lux-card relative w-full max-w-lg p-7 md:p-8"
      >
        <button
          type="button"
          onClick={() => !submitting && onClose()}
          aria-label="Close"
          className="absolute top-4 right-4 text-[var(--lux-muted)] hover:text-[var(--lux-ink)]"
          data-testid="crm-request-close"
        >
          <X className="w-4 h-4" strokeWidth={1.8} />
        </button>

        <div className="lux-eyebrow mb-3">Integration request</div>
        <h3 className="lux-display text-2xl md:text-[28px] tracking-tight leading-[1.15] mb-2">
          Request your CRM
        </h3>
        <p className="text-[14px] mb-6" style={{ color: "var(--lux-ink-soft)" }}>
          Tell us which CRM you use and we&apos;ll prioritise the integration. Most builds ship within a fortnight.
        </p>

        <div className="space-y-4">
          <Field label="CRM name *" testid="crm-field-name">
            <input
              type="text"
              value={crmName}
              onChange={(e) => setCrmName(e.target.value)}
              placeholder="e.g. Reapit, HubSpot, custom in-house tool"
              className="lux-input"
              maxLength={120}
              data-testid="crm-input-name"
            />
          </Field>

          <Field label="Company" testid="crm-field-company">
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Your agency or brokerage (optional)"
              className="lux-input"
              maxLength={200}
              data-testid="crm-input-company"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name *" testid="crm-field-contact-name">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Full name"
                className="lux-input"
                maxLength={120}
                data-testid="crm-input-contact-name"
              />
            </Field>
            <Field label="Email *" testid="crm-field-contact-email">
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="you@agency.com"
                className="lux-input"
                maxLength={200}
                data-testid="crm-input-contact-email"
              />
            </Field>
          </div>

          <Field label="Notes (optional)" testid="crm-field-details">
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Listing volume, API access, anything we should know"
              className="lux-input min-h-[96px] resize-y"
              maxLength={2000}
              data-testid="crm-input-details"
            />
          </Field>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="lux-btn-ghost !py-2.5 !px-4 text-sm"
            disabled={submitting}
            data-testid="crm-request-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !valid}
            className="lux-btn-primary !py-2.5 !px-5 text-sm disabled:opacity-50"
            data-testid="crm-request-submit"
          >
            {submitting ? "Sending…" : "Send request"}
            {!submitting && <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, children, testid }: { label: string; children: ReactNode; testid?: string }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[11px] font-medium tracking-[0.14em] uppercase mb-1.5" style={{ color: "var(--lux-muted)" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

/* ============================================================
 * 8 · Feature grid
 * ============================================================ */
function FeatureGrid() {
  const items = [
    { icon: Layers, t: "Bulk pipeline", d: "Upload 50 photographs at once. Live per-image progress. Zip download when ready." },
    { icon: Zap, t: "30-second turnaround", d: "Most photos render in under a minute. Heavy batches run in parallel behind the scenes." },
    { icon: ShieldCheck, t: "Architecture locked", d: "Windows, walls, ceilings and floor plans never move. Only furnishings change." },
    { icon: ImageIcon, t: "Per-image room types", d: "Tell us what each image is — kitchen, bedroom, lobby — and we'll style accordingly." },
    { icon: Globe2, t: "Region-aware styling", d: "Mediterranean villa? Mid-century LA? Dubai penthouse? The model adapts to context." },
    { icon: Lock, t: "Your images, your data", d: "Originals stored privately. Optional permanent delete after 30 days." },
  ];
  return (
    <section className="border-t border-[var(--lux-line)] py-24 md:py-32 bg-white/40" data-testid="feature-grid">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-2xl mb-16 md:mb-20">
          <div className="lux-eyebrow mb-4">Built into the studio</div>
          <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
            Quietly powerful.<br /><span className="italic font-light">Intentionally simple.</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.t}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
              whileHover={{ y: -3 }}
              className="lux-card p-7"
            >
              <it.icon className="w-5 h-5 mb-6" strokeWidth={1.5} style={{ color: "var(--lux-gold-deep)" }} />
              <div className="lux-display text-[1.4rem] mb-2.5">{it.t}</div>
              <p className="text-[14px] leading-[1.65]" style={{ color: "var(--lux-ink-soft)" }}>{it.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 9 · Testimonials
 * ============================================================ */
function TestimonialsSection() {
  return (
    <section className="py-24 md:py-32" data-testid="testimonials">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-2xl mb-16 md:mb-20">
          <div className="lux-eyebrow mb-4">From the studios that ship</div>
          <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
            What teams say<br /><span className="italic font-light">about the work.</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
              whileHover={{ y: -3 }}
              className="lux-card p-7 md:p-8"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-1.5 mb-6" aria-label={`${t.rating} star rating`}>
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <Star key={idx} className="w-3.5 h-3.5 fill-[var(--lux-gold)] text-[var(--lux-gold)]" strokeWidth={0} />
                ))}
              </div>
              <blockquote className="lux-display text-[1.2rem] md:text-[1.3rem] leading-[1.4] mb-8" style={{ color: "var(--lux-ink)" }}>
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-1 ring-[var(--lux-line)]" loading="lazy" />
                <div>
                  <div className="text-[14px] font-medium">{t.name}</div>
                  <div className="text-[12px]" style={{ color: "var(--lux-muted)" }}>{t.role}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 10 · Pricing preview — live from /api/credits/packs
 * ============================================================ */
function PricingPreview() {
  const [packs, setPacks] = useState<Pack[] | null>(null);
  const [err, setErr] = useState(false);
  const { formatMoney, currency } = useCurrency();
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/credits/packs`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => setPacks(Array.isArray(d?.packs) ? d.packs : []))
      .catch(() => setErr(true));
  }, []);

  // Filter to the 3 tiers we want to surface on the homepage: starter, pro, studio.
  const featured = (packs || []).filter((p) => ["starter", "pro", "studio"].includes(p.id));

  return (
    <section id="pricing" className="border-t border-[var(--lux-line)] py-24 md:py-32" data-testid="pricing-preview">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="max-w-3xl mb-14 md:mb-20">
          <div className="lux-eyebrow mb-4">Pricing, simply</div>
          <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
            Pay per photograph.<br /><span className="italic font-light">No subscription required.</span>
          </h2>
          <p className="text-[15px] md:text-[16px] leading-[1.7] mt-6 max-w-2xl" style={{ color: "var(--lux-ink-soft)" }}>
            Top up when you need to. Credits last six months. Subscriptions optional. Cancel anytime.
          </p>
        </motion.div>

        {err && (
          <div className="lux-card p-8 text-center" style={{ color: "var(--lux-ink-soft)" }}>
            Pricing temporarily unavailable. <Link to="/pricing" className="lux-underline-gold">See full pricing →</Link>
          </div>
        )}

        {!err && !packs && (
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {[1, 2, 3].map((i) => <div key={i} className="lux-card aspect-[5/6] animate-pulse opacity-50" />)}
          </div>
        )}

        {!err && packs && (
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {featured.map((p, i) => {
              const popular = p.badge === "Popular";
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                  whileHover={{ y: -4 }}
                  data-testid={`pricing-card-${p.id}`}
                  className={popular ? "lux-card-dark p-8 md:p-10 relative" : "lux-card p-8 md:p-10 relative"}
                >
                  {popular && (
                    <div className="absolute top-4 right-4 text-[10px] tracking-[0.18em] uppercase font-semibold px-3 py-1 rounded-full" style={{ background: "var(--lux-gold)", color: "#1A1A18" }}>
                      Most chosen
                    </div>
                  )}
                  <div className="lux-eyebrow mb-3" style={{ color: popular ? "var(--lux-gold)" : undefined }}>{p.label}</div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="lux-display text-[3.4rem] leading-none">{formatMoney(p.price_usd, { decimals: p.price_usd >= 100 ? 0 : 2 })}</span>
                    <span className="text-[13px]" style={{ color: popular ? "rgba(255,255,255,0.55)" : "var(--lux-muted)" }}>{currency}</span>
                  </div>
                  <div className="text-[13px] mb-8" style={{ color: popular ? "rgba(255,255,255,0.65)" : "var(--lux-muted)" }}>
                    {p.credits} photographs · {formatMoney(p.per_credit_usd)} per photo
                  </div>
                  <Link
                    to="/register"
                    className={popular ? "lux-btn-primary w-full justify-center" : "lux-btn-ghost w-full justify-center"}
                    style={popular ? { background: "var(--lux-gold)", borderColor: "var(--lux-gold)", color: "#1A1A18" } : undefined}
                    data-testid={`pricing-cta-${p.id}`}
                  >
                    Choose {p.label} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/pricing" className="lux-underline-gold text-[14px] font-medium" data-testid="see-all-pricing">
            See all plans &amp; subscriptions →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 10b · Roadmap teaser — "Coming soon" + "Recently shipped"
 * ============================================================ */
interface LandingRoadmapItem {
  id: string;
  title: string;
  status: "planned" | "in_progress" | "completed";
}

function roadmapStatusPill(status: LandingRoadmapItem["status"]): { label: string; cls: string } {
  if (status === "completed") return { label: "Shipped", cls: "text-emerald-700 bg-emerald-50" };
  if (status === "in_progress") return { label: "In progress", cls: "text-amber-700 bg-amber-50" };
  return { label: "Planned", cls: "text-[var(--lux-ink-soft)] bg-black/5" };
}

function LandingRoadmapSection() {
  const [comingSoon, setComingSoon] = useState<LandingRoadmapItem[]>([]);
  const [completed, setCompleted] = useState<LandingRoadmapItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.get<{ coming_soon: LandingRoadmapItem[]; completed: LandingRoadmapItem[] }>("/roadmap/public")
      .then(({ data }) => {
        if (cancelled) return;
        setComingSoon((data.coming_soon || []).slice(0, 5));
        setCompleted((data.completed || []).slice(0, 5));
      })
      .catch(() => { /* hide section on error */ });
    return () => { cancelled = true; };
  }, []);

  // Hide entirely when there's nothing to show.
  if (comingSoon.length === 0 && completed.length === 0) return null;

  const Col = ({ heading, items }: { heading: string; items: LandingRoadmapItem[] }) => (
    <div>
      <div className="lux-eyebrow mb-4">{heading}</div>
      <ul className="space-y-3">
        {items.length === 0 && <li className="text-[14px]" style={{ color: "var(--lux-ink-soft)" }}>Nothing yet.</li>}
        {items.map((it) => {
          const pill = roadmapStatusPill(it.status);
          return (
            <li key={it.id} className="flex items-center justify-between gap-3">
              <span className="text-[15px]" style={{ color: "var(--lux-ink)" }}>{it.title}</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${pill.cls}`}>{pill.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <section id="roadmap" className="py-24 md:py-32 border-t border-[var(--lux-line)]" data-testid="landing-roadmap-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
          <div className="md:col-span-6">
            <div className="lux-eyebrow mb-4">Product Roadmap</div>
            <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
              What we&apos;re building<br/><span className="italic font-light">next.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-[15px] md:text-[16px] leading-[1.7]" style={{ color: "var(--lux-ink-soft)" }}>
              We ship in the open. Vote on what matters to you and see what&apos;s coming.
            </p>
            <Link to="/roadmap" className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold lux-underline-gold" style={{ color: "var(--lux-gold-deep)" }} data-testid="landing-roadmap-view-all">
              View full roadmap →
            </Link>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-10 md:gap-16">
          <Col heading="Coming soon" items={comingSoon} />
          <Col heading="Recently shipped" items={completed} />
        </motion.div>
      </div>
    </section>
  );
}


/* ============================================================
 * 11 · FAQ
 * ============================================================ */
function FaqSection() {
  return (
    <section id="faq" className="border-t border-[var(--lux-line)] bg-white/40 py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp} className="mb-12 md:mb-16">
          <div className="lux-eyebrow mb-4">Questions, answered</div>
          <h2 className="lux-display text-4xl md:text-5xl leading-[1.06] tracking-tight">
            Honest answers.<br /><span className="italic font-light">No fine print.</span>
          </h2>
        </motion.div>
        <div className="border-t border-[var(--lux-line-strong)]">
          {FAQS.map((item, i) => (
            <FaqItem key={i} idx={i} q={item.q} a={item.a} />
          ))}
        </div>
        <div className="mt-12 text-[14px] text-center" style={{ color: "var(--lux-ink-soft)" }}>
          Still curious?{" "}
          <a href="mailto:hello@stagingstudio.app" className="lux-underline-gold font-medium" data-testid="faq-contact-link">
            hello@stagingstudio.app
          </a>
          {" — "}we usually reply within the hour.
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[var(--lux-line-strong)]" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-6 md:py-7 text-left group"
        aria-expanded={open}
      >
        <span className="lux-display text-[1.15rem] md:text-[1.3rem] tracking-tight pr-4 group-hover:text-[var(--lux-gold-deep)] transition-colors">{q}</span>
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-8 h-8 rounded-full border border-[var(--lux-line-strong)] grid place-items-center shrink-0 group-hover:border-[var(--lux-ink)] transition-colors"
        >
          {open ? <Minus className="w-3.5 h-3.5" strokeWidth={2} /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-6 md:pb-7 -mt-2 text-[14.5px] md:text-[15px] leading-[1.7] max-w-3xl" style={{ color: "var(--lux-ink-soft)" }} data-testid={`faq-answer-${idx}`}>
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================
 * 12 · Final CTA
 * ============================================================ */
function FinalCTA() {
  return (
    <section className="py-24 md:py-32" data-testid="final-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          {...fadeUp}
          className="lux-card-dark relative overflow-hidden p-12 md:p-20"
        >
          <div className="lux-orb lux-orb-gold lux-orb-drift-1 absolute" style={{ width: 600, height: 600, top: -200, right: -200, opacity: 0.5 }} aria-hidden />
          <div className="relative grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="lux-eyebrow mb-6" style={{ color: "var(--lux-gold)" }}>The studio is open</div>
              <h2 className="lux-display text-4xl md:text-[3.4rem] leading-[1.05] tracking-tight text-white">
                Sell the dream.<br />
                <span className="italic font-light">Honestly.</span>
              </h2>
              <p className="mt-6 text-white/65 max-w-xl text-[15px] md:text-[16px] leading-[1.7]">
                Three photographs free. No credit card. Cancel anytime. Built in Dubai for the world&apos;s most ambitious listings.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <MagneticButton to="/register" testid="final-cta-btn" primary>
                Create your studio <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </MagneticButton>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================================
 * 13 · Luxury footer
 * ============================================================ */
function LuxuryFooter() {
  return (
    <footer className="border-t border-[var(--lux-line)] py-14" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <BrandLogo variant="wide" className="h-10 w-auto mb-5" />
            <p className="text-[14px] leading-[1.7] max-w-md" style={{ color: "var(--lux-ink-soft)" }}>
              Editorial-grade virtual staging for the world&apos;s most ambitious real-estate listings. Made in Dubai.
            </p>
          </div>
          <div className="md:col-span-2 md:col-start-7">
            <div className="lux-eyebrow mb-4">Studio</div>
            <ul className="space-y-2.5 text-[13.5px]" style={{ color: "var(--lux-ink-soft)" }}>
              <li><Link to="/pricing" className="lux-underline-gold" data-testid="footer-pricing-link">Pricing</Link></li>
              <li><a href="#faq" className="lux-underline-gold" data-testid="footer-faq-link">FAQ</a></li>
              <li><Link to="/register" className="lux-underline-gold">Start free</Link></li>
              <li><Link to="/login" className="lux-underline-gold">Sign in</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="lux-eyebrow mb-4">Legal</div>
            <ul className="space-y-2.5 text-[13.5px]" style={{ color: "var(--lux-ink-soft)" }}>
              <li><Link to="/terms" className="lux-underline-gold" data-testid="footer-terms-link">Terms</Link></li>
              <li><Link to="/privacy" className="lux-underline-gold" data-testid="footer-privacy-link">Privacy</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="lux-eyebrow mb-4">Contact</div>
            <ul className="space-y-2.5 text-[13.5px]" style={{ color: "var(--lux-ink-soft)" }}>
              <li><a href="mailto:hello@stagingstudio.app" className="lux-underline-gold" data-testid="footer-contact-link">hello@stagingstudio.app</a></li>
              <li>Dubai, UAE</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[var(--lux-line)] pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12px]" style={{ color: "var(--lux-muted)" }}>
          <div>© {new Date().getFullYear()} Stage Studios · Crafted for luxury listings</div>
          <div className="text-[11px] tracking-[0.22em] uppercase">Made in Dubai</div>
        </div>
      </div>
    </footer>
  );
}
